import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { 
  FaGavel, 
  FaUserTie, 
  FaBalanceScale,
  FaUserFriends,
  FaBuilding,
  FaUserCheck,
  FaChartLine,
  FaAward
} from 'react-icons/fa';

const Counter = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(0);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth);
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  useEffect(() => {
    if (inView) {
      setIsVisible(true);
    }
  }, [inView]);

  // Utilisation d'une couleur d'accentuation unifiée pour un rendu premium
  const premiumAccent = "#C1A68D";

  const counters = [
    { 
      icon: <FaGavel />, 
      title: "Expertises", 
      number: 8,
      suffix: "+",
      description: "Domaines de spécialisation",
    },
    { 
      icon: <FaUserTie />, 
      title: "Avocats", 
      number: 9,
      suffix: "+",
      description: "Professionnels diplômés",
    },
    { 
      icon: <FaBalanceScale />, 
      title: "Juristes", 
      number: 15,
      suffix: "+",
      description: "Experts juridiques",
    },
    { 
      icon: <FaUserFriends />, 
      title: "Consultants", 
      number: 6,
      suffix: "+",
      description: "Spécialistes externes",
    },
    { 
      icon: <FaBuilding />, 
      title: "Entreprises", 
      number: 27,
      suffix: "+",
      description: "Sociétés accompagnées",
    },
    { 
      icon: <FaUserCheck />, 
      title: "Particuliers", 
      number: 120,
      suffix: "+",
      description: "Clients physiques",
    },
    { 
      icon: <FaChartLine />, 
      title: "Satisfaction", 
      number: 98,
      suffix: "%",
      description: "Taux de recommandation",
    },
    { 
      icon: <FaAward />, 
      title: "Expérience", 
      number: 2,
      suffix: "+",
      description: "Années d'excellence",
    },
  ];

  return (
    <div className="counter-section" ref={ref}>
      {/* Texture de fond luxueuse */}
      <div className="noise-overlay" />
      
      {/* Poussière dorée / Particules */}
      <div className="background-effects">
        <div className="particle" />
        <div className="particle" />
        <div className="particle" />
        <div className="particle" />
        <div className="particle" />
      </div>

      {/* En-tête */}
      <motion.div 
        className="section-header"
        initial={{ opacity: 0, y: -30 }}
        animate={isVisible ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
      >
        <h2 className="section-title">Notre Impact</h2>
        <p className="section-subtitle">
          Des chiffres qui témoignent de notre engagement, de notre rigueur et de notre expertise.
        </p>
      </motion.div>

      {/* Grille de compteurs */}
      <div className="counter-grid">
        <motion.div 
          className="counter-wrapper"
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          variants={{
            hidden: { opacity: 0 },
            visible: { 
              opacity: 1,
              transition: { staggerChildren: 0.1 }
            }
          }}
        >
          {counters.map((counter, index) => (
            <motion.div
              key={index}
              className="counter-card"
              variants={{
                hidden: { opacity: 0, y: 30, scale: 0.98 },
                visible: { 
                  opacity: 1, y: 0, scale: 1,
                  transition: { duration: 0.6, ease: "easeOut" }
                }
              }}
              whileHover={{ 
                y: -5,
                borderColor: '#C1A68D',
                boxShadow: '0 15px 30px rgba(0, 0, 0, 0.5)'
              }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Effet de lueur subtil beige */}
              <div className="card-glow" />
              
              {/* Icône */}
              <motion.div 
                className="icon-wrapper"
                animate={isVisible ? { scale: [1, 1.05, 1] } : {}}
                transition={{ delay: 0.3 + index * 0.1, duration: 1.5, ease: "easeInOut" }}
              >
                <div className="icon-content">
                  {counter.icon}
                </div>
              </motion.div>

              {/* Nombre */}
              <div className="number-container">
                {isVisible && (
                  <CountUp
                    start={0}
                    end={counter.number}
                    duration={2.5}
                    delay={index * 0.1}
                    separator=" "
                    className="counter-number"
                  />
                )}
                <span className="counter-suffix">{counter.suffix}</span>
              </div>

              {/* Titre */}
              <h3 className="counter-title">{counter.title}</h3>
              
              {/* Description */}
              <p className="counter-description">{counter.description}</p>

              {/* Barre de progression animée */}
              <motion.div 
                className="progress-bar"
                initial={{ width: 0 }}
                animate={isVisible ? { width: '100%' } : {}}
                transition={{ 
                  duration: 1.5, 
                  delay: index * 0.1 + 0.3,
                  ease: "easeInOut"
                }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Note de bas de page */}
      <motion.div 
        className="counter-footer"
        initial={{ opacity: 0 }}
        animate={isVisible ? { opacity: 1 } : {}}
        transition={{ delay: 1.2 }}
      >
        <p className="footer-text">
          * Données actualisées au dernier trimestre
        </p>
      </motion.div>

      <style jsx>{`
        .counter-section {
          position: relative;
          padding: 6rem 1rem;
          background: linear-gradient(135deg, #0A0A0A 0%, #161616 50%, #050505 100%);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .noise-overlay {
          position: absolute;
          inset: 0;
          background-image: url('https://www.transparenttextures.com/patterns/stardust.png');
          opacity: 0.15;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 0;
        }

        /* Effets de fond (Particules Dorées/Beige) */
        .background-effects {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 1;
        }

        .particle {
          position: absolute;
          background: radial-gradient(circle, rgba(193, 166, 141, 0.4) 0%, transparent 70%);
          border-radius: 50%;
          animation: float 20s infinite linear;
          filter: blur(4px);
        }

        .particle:nth-child(1) { width: 100px; height: 100px; top: 10%; left: 10%; opacity: 0.5; animation-delay: 0s; }
        .particle:nth-child(2) { width: 150px; height: 150px; top: 60%; right: 10%; opacity: 0.3; animation-delay: -5s; }
        .particle:nth-child(3) { width: 80px; height: 80px; bottom: 20%; left: 20%; opacity: 0.4; animation-delay: -10s; }
        .particle:nth-child(4) { width: 120px; height: 120px; top: 30%; right: 25%; opacity: 0.3; animation-delay: -15s; }
        .particle:nth-child(5) { width: 90px; height: 90px; bottom: 40%; left: 60%; opacity: 0.4; animation-delay: -7s; }

        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-30px) scale(1.1); }
        }

        /* En-tête */
        .section-header {
          text-align: center;
          margin-bottom: 4rem;
          position: relative;
          z-index: 2;
          padding: 0 1rem;
        }

        .section-title {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: #F5F0E6;
          text-transform: uppercase;
          letter-spacing: 2px;
          line-height: 1.2;
        }

        .section-subtitle {
          font-size: 1.1rem;
          color: #C1A68D;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
          font-weight: 300;
        }

        @media (max-width: 768px) {
          .section-title { font-size: 2.2rem; }
          .section-subtitle { font-size: 1rem; }
        }

        /* Grille de compteurs */
        .counter-grid {
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
          padding: 0 1rem;
          z-index: 2;
          position: relative;
        }

        .counter-wrapper {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }

        /* Carte de compteur */
        .counter-card {
          position: relative;
          background: rgba(26, 26, 26, 0.5);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(193, 166, 141, 0.15);
          border-radius: 4px;
          padding: 2.5rem 1.5rem;
          text-align: center;
          transition: all 0.4s ease;
          overflow: hidden;
          min-height: 240px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .card-glow {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at center, rgba(193, 166, 141, 0.05) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 0.4s ease;
          z-index: 0;
        }

        .counter-card:hover .card-glow {
          opacity: 1;
        }

        /* Icône */
        .icon-wrapper {
          position: relative;
          width: 65px;
          height: 65px;
          margin-bottom: 1.5rem;
          z-index: 1;
        }

        .icon-content {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.8rem;
          color: #C1A68D;
          background: rgba(193, 166, 141, 0.05);
          border-radius: 50%;
          border: 1px solid rgba(193, 166, 141, 0.2);
          transition: all 0.3s ease;
        }

        .counter-card:hover .icon-content {
          background: rgba(193, 166, 141, 0.1);
          border-color: #C1A68D;
          color: #F5F0E6;
        }

        /* Nombre */
        .number-container {
          display: flex;
          align-items: baseline;
          justify-content: center;
          margin-bottom: 0.5rem;
          z-index: 1;
        }

        .counter-number {
          font-size: 3rem;
          font-weight: 700;
          color: #C1A68D;
          font-family: 'Times New Roman', serif;
          line-height: 1;
        }

        .counter-suffix {
          font-size: 1.8rem;
          font-weight: 500;
          color: #C1A68D;
          margin-left: 0.2rem;
          font-family: 'Times New Roman', serif;
        }

        /* Titre */
        .counter-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #F5F0E6;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          z-index: 1;
        }

        /* Description */
        .counter-description {
          font-size: 0.9rem;
          color: #D4C5B0;
          line-height: 1.4;
          font-weight: 300;
          z-index: 1;
        }

        /* Barre de progression */
        .progress-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: #C1A68D;
          width: 0;
        }

        /* Pied de page */
        .counter-footer {
          text-align: center;
          margin-top: 3.5rem;
          position: relative;
          z-index: 2;
        }

        .footer-text {
          color: #D4C5B0;
          font-size: 0.85rem;
          font-style: italic;
          opacity: 0.6;
        }

        /* Responsive Styles */
        @media (min-width: 768px) {
          .counter-wrapper { grid-template-columns: repeat(4, 1fr); gap: 1.5rem; }
          .counter-card { padding: 2.5rem 1.5rem; }
          .counter-number { font-size: 3.2rem; }
          .counter-title { font-size: 1.2rem; }
        }

        @media (min-width: 1024px) {
          .counter-section { padding: 8rem 2rem; }
          .counter-wrapper { gap: 2rem; }
          .counter-card { padding: 3rem 2rem; }
          .icon-wrapper { width: 75px; height: 75px; }
          .icon-content { font-size: 2.2rem; }
          .counter-number { font-size: 3.8rem; }
          .counter-title { font-size: 1.3rem; }
        }

        @media (max-width: 480px) {
          .counter-wrapper { grid-template-columns: 1fr; gap: 1rem; }
          .counter-number { font-size: 2.8rem; }
          .counter-card { min-height: 200px; padding: 2rem 1rem; }
        }

        /* Optimisation tactiles */
        @media (hover: none) and (pointer: coarse) {
          .counter-card:hover { transform: none; box-shadow: none; border-color: rgba(193, 166, 141, 0.15); }
        }
      `}</style>
    </div>
  );
};

export default Counter;